import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Card } from "@mui/material";
import { handleLogout } from "../config/axios";
import decryptData from "../decrypt";

// Define TypeScript interfaces for our data structures
interface Address {
  INDIRIZZO: string;
  CAP: string;
  CITTA: string;
  PROVINCIA: string;
  TEL: string;
  EMAIL: string;
  CODICE_INDIRIZZO: string;
  CODICE_CLIENTE: string;
}

interface FinancialData {
  CODICE_CLIENTE: string;
  RAGIONE_SOCIALE: string;
  CODICE_AGENTE: string;
  ORDINI: number;
  BOLLE_IN_ATTESA_DI_FATTURA: number;
  FIDO: number;
  CAMBIALE: number;
  TITOLI_A_RISCHIO: number;
  ESPOSIZIONE_RIBA: number;
  SALDO_CONTABILE: number;
  TITOLI: number;
  ORDINI_PROGRAMMATI: number;
  INSOLUTI: number;
  ALTRO: number;
}

interface FinancialDetail {
  DATA_SCADENZA: string | number;
  IMPORTO?: number;
  NUMERO_RICEVUTA: string;
  TIPO: string;
}

interface CustomerData {
  addresses: Address[];
  financialData: FinancialData | null;
  financialDetails: FinancialDetail[];
}

export default function FidoDetail() {
  const carousel = useRef<HTMLDivElement>(null);
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [secondCollapseActive, setSecondCollapseActive] =
    useState<boolean>(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Get current theme from Redux store
  const currentTheme = useSelector((state: any) => state.menu?.theme);
  const [theme, setTheme] = useState(currentTheme || "light");

  // Update theme when it changes in the store
  useEffect(() => {
    if (currentTheme) {
      setTheme(currentTheme);
    }
  }, [currentTheme]);

  // Theme-based styling classes
  const baseClasses = {
    background: theme === "dark" ? "" : "bg-gray-50",
    card:
      theme === "dark"
        ? "bg-black-500 border-gray-700"
        : "bg-white border-gray-200",
    cardHeader: theme === "dark" ? "text-white" : "text-gray-900",
    text: theme === "dark" ? "text-gray-300" : "text-gray-900",
    textSecondary: theme === "dark" ? "text-gray-400" : "text-gray-600",
    highlight: theme === "dark" ? "text-blue-400" : "text-blue-600",
    hover: theme === "dark" ? "hover:bg-gray-700" : "hover:bg-gray-50",
    border: theme === "dark" ? "border-gray-700" : "border-gray-200",
    inputBg: theme === "dark" ? "" : "bg-gray-50",
  };

  // Fetch all customer data
  useEffect(() => {
    if (id) {
      fetchCustomerDetails(id);
    }
  }, [id]);

  // Initialize carousel when customer data is loaded
  useEffect(() => {
    if (customer && carousel.current) {
      import("preline/preline").then(({ HSCarousel }) => {
        HSCarousel.autoInit();
      });
    }
  }, [customer]);

  // Setup table horizontal scroll functionality
  useEffect(() => {
    const tableContainer = tableRef.current;
    if (!tableContainer) return;

    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    const mouseDown = (e: MouseEvent) => {
      isDown = true;
      startX = e.pageX - tableContainer.offsetLeft;
      scrollLeft = tableContainer.scrollLeft;
      tableContainer.style.cursor = "grabbing";
    };

    const mouseLeave = () => {
      isDown = false;
      tableContainer.style.cursor = "grab";
    };

    const mouseUp = () => {
      isDown = false;
      tableContainer.style.cursor = "grab";
    };

    const mouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - tableContainer.offsetLeft;
      const walk = (x - startX) * 2;
      tableContainer.scrollLeft = scrollLeft - walk;
    };

    tableContainer.addEventListener("mousedown", mouseDown);
    tableContainer.addEventListener("mouseleave", mouseLeave);
    tableContainer.addEventListener("mouseup", mouseUp);
    tableContainer.addEventListener("mousemove", mouseMove);

    return () => {
      tableContainer.removeEventListener("mousedown", mouseDown);
      tableContainer.removeEventListener("mouseleave", mouseLeave);
      tableContainer.removeEventListener("mouseup", mouseUp);
      tableContainer.removeEventListener("mousemove", mouseMove);
    };
  }, []);

  const fetchCustomerDetails = async (customerId: string) => {
    try {
      setLoading(true);

      // Prepare request options with authentication
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ codice_cliente: customerId }),
      };

      // Make all three API calls in parallel for better performance
      const [
        addressesResponse,
        financialDataResponse,
        financialDetailsResponse,
      ] = await Promise.all([
        fetch("/nodeapi/indirizzi", requestOptions),
        fetch("/nodeapi/utenti", requestOptions),
        fetch("/nodeapi/situazione_finanziaria_dettaglio", requestOptions),
      ]);


      if (addressesResponse.status !== 200 && addressesResponse.status !== 201) {
        handleLogout(
          addressesResponse,
          "/nodeapi/indirizzi",
          JSON.stringify("")
        );
        return;
      }
      if (financialDataResponse.status !== 200 && financialDataResponse.status !== 201) {
        handleLogout(
          financialDataResponse,
          "/nodeapi/utenti",
          JSON.stringify("")
        );
        return;
      }
      if (financialDetailsResponse.status !== 200 && financialDetailsResponse.status !== 201) {
        handleLogout(
          financialDetailsResponse,
          "/nodeapi/situazione_finanziaria_dettaglio",
          JSON.stringify("")
        );
        return;
      }

      // Parse JSON responses
      const addressesData = await addressesResponse.json();
      const decrypted = decryptData(
        addressesData.encryptedData,
        addressesData.encryptedKey,
        addressesData.iv
      );
      const decryptedJson = JSON.parse(decrypted);

      console.log("addressesData: ", decryptedJson);

      const financialData = await financialDataResponse.json();
      const decryptedFin = decryptData(
        financialData.encryptedData,
        financialData.encryptedKey,
        financialData.iv
      );
      const decryptedJsonFin = JSON.parse(decryptedFin);

      console.log("financialData: ", decryptedJsonFin);

      const financialDetailsData = await financialDetailsResponse.json();
      const decryptedDetail = decryptData(
        financialDetailsData.encryptedData,
        financialDetailsData.encryptedKey,
        financialDetailsData.iv
      );
      const decryptedJsonDetail = JSON.parse(decryptedDetail);

      console.log("financialDetailsData: ", decryptedJsonDetail);

      // Check if all responses are successful
      if (
        decryptedJson.status === "success" &&
        decryptedJsonFin.status === "success" &&
        decryptedJsonDetail.status === "success"
      ) {
        // Combine all data into a single customer object
        setCustomer({
          addresses: decryptedJson.data,
          financialData: decryptedJsonFin.data[0] || null,
          financialDetails: decryptedJsonDetail.data || [],
        });
      } else {
        setCustomer(null);
      }
    } catch (e) {
      console.error("Error fetching customer details:", e);
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  const showSecondCarousel = () => {
    setSecondCollapseActive(!secondCollapseActive);
  };

  // Get primary address (first in the list)
  const primaryAddress = customer?.addresses[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg text-gray-600">
          Nessun dettaglio cliente trovato
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${baseClasses.background}`}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="relative sm:max-w-4xl px-5 sm:px-12 mx-auto flex flex-col justify-between mb-12 md:mt-50">
          <div className="sm:max-w-4xl px-5 sm:px-12 mx-auto flex-col justify-between  mt-cu-md md:mt-20"></div>
        </div>
        <div className={`rounded-lg p-6 mb-6 border ${baseClasses.card}`}>
          <div className="flex items-center space-x-4 mb-8">
            <button
              onClick={() => navigate(-1)}
              className={`p-2 rounded-full ${baseClasses.hover} transition-colors`}
            >
              <span
                className={`material-symbols-outlined ${baseClasses.highlight}`}
              >
                arrow_back
              </span>
            </button>
            <h1 className={`text-3xl font-bold ${baseClasses.cardHeader}`}>
              Cliente{" "}
              <span className={baseClasses.highlight}>
                {customer.financialData?.RAGIONE_SOCIALE}
              </span>
            </h1>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                label: "Codice Cliente",
                value: customer.financialData?.CODICE_CLIENTE,
              },
              { label: "E-mail", value: primaryAddress?.EMAIL || "N/A" },
              { label: "Telefono", value: primaryAddress?.TEL || "N/A" },
              { label: "Città", value: primaryAddress?.CITTA || "N/A" },
            ].map((item, index) => (
              <div
                key={index}
                className={`rounded-lg p-4 ${baseClasses.inputBg}`}
              >
                <div className={`text-sm mb-1 ${baseClasses.textSecondary}`}>
                  {item.label}
                </div>
                <div className={`text-lg font-medium ${baseClasses.highlight}`}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Customer Details Section */}
        <div className={`rounded-lg mb-6 border ${baseClasses.card}`}>
          <div className={`px-6 py-4 border-b ${baseClasses.border}`}>
            <h2 className={`text-lg font-medium ${baseClasses.cardHeader}`}>
              Dati Anagrafici
            </h2>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  label: "Ragione Sociale",
                  value: customer.financialData?.RAGIONE_SOCIALE,
                },
                { label: "Indirizzo", value: primaryAddress?.INDIRIZZO },
                { label: "Provincia", value: primaryAddress?.PROVINCIA },
                { label: "CAP", value: primaryAddress?.CAP },
                {
                  label: "Codice Agente",
                  value: customer.financialData?.CODICE_AGENTE,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`rounded-lg p-4 ${baseClasses.inputBg}`}
                >
                  <div className={`text-sm mb-1 ${baseClasses.textSecondary}`}>
                    {item.label}
                  </div>
                  <div
                    className={`text-lg font-medium ${baseClasses.highlight}`}
                  >
                    {item.value || "N/A"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Financial Data Section */}
        {/*         <div className={`rounded-lg mb-6 border ${baseClasses.card}`}>
          <div className={`px-6 py-4 border-b ${baseClasses.border}`}>
            <h2 className={`text-lg font-medium ${baseClasses.cardHeader}`}>
              Dati contabili
            </h2>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  label: "Saldo Contabile",
                  value: Math.floor(
                    Number(customer.financialData?.SALDO_CONTABILE || 0)
                  ).toLocaleString("it-IT", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: 0,
                  }),
                },
                {
                  label: "Fido",
                  value: Math.floor(
                    Number(customer.financialData?.FIDO || 0)
                  ).toLocaleString("it-IT", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: 0,
                  }),
                },
                {
                  label: "Fido Utilizzato",
                  value: Math.floor(
                    Number(customer.financialData?.SALDO_CONTABILE || 0) +
                      Number(
                        customer.financialData?.BOLLE_IN_ATTESA_DI_FATTURA || 0
                      ) +
                      Number(customer.financialData?.ORDINI || 0) +
                      Number(customer.financialData?.TITOLI || 0) +
                      Number(customer.financialData?.ESPOSIZIONE_RIBA || 0) +
                      Number(customer.financialData?.CAMBIALE || 0) +
                      Number(customer.financialData?.TITOLI_A_RISCHIO || 0)
                  ).toLocaleString("it-IT", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: 0,
                  }),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`rounded-lg p-4 ${baseClasses.inputBg}`}
                >
                  <div className={`text-sm mb-1 ${baseClasses.textSecondary}`}>
                    {item.label}
                  </div>
                  <div
                    className={`text-lg font-medium ${baseClasses.highlight}`}
                  >
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div> */}

        {/* Additional Info Section */}
        {/*         <div className={`rounded-lg mb-6 border ${baseClasses.card}`}>
          <button
            onClick={showSecondCarousel}
            className={`w-full px-6 py-4 flex items-center justify-between ${baseClasses.hover} transition-colors`}
          >
            <span className={`text-lg font-medium ${baseClasses.cardHeader}`}>
              {secondCollapseActive
                ? "Meno Informazioni"
                : "Maggiori Informazioni"}
            </span>
            <span
              className={`material-symbols-outlined ${baseClasses.highlight}`}
            >
              {secondCollapseActive ? "expand_less" : "expand_more"}
            </span>
          </button>

          {secondCollapseActive && (
            <div className={`px-6 py-4 border-t ${baseClasses.border}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  {
                    label: "Bolle Att. Fat.",
                    value: Math.floor(
                      Number(
                        customer.financialData?.BOLLE_IN_ATTESA_DI_FATTURA || 0
                      )
                    ).toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                    }),
                  },
                  {
                    label: "Ordini Programmati",
                    value: Math.floor(
                      Number(customer.financialData?.ORDINI_PROGRAMMATI || 0)
                    ).toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                    }),
                  },
                  {
                    label: "Ordini",
                    value: Math.floor(
                      Number(customer.financialData?.ORDINI || 0)
                    ).toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                    }),
                  },
                  {
                    label: "Esposizione Riba",
                    value: Math.floor(
                      Number(customer.financialData?.ESPOSIZIONE_RIBA || 0)
                    ).toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                    }),
                  },
                  {
                    label: "Cambiale",
                    value: Math.floor(
                      Number(customer.financialData?.CAMBIALE || 0)
                    ).toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                    }),
                  },
                  {
                    label: "Insoluti",
                    value: Math.floor(
                      Number(customer.financialData?.INSOLUTI || 0)
                    ).toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                    }),
                  },
                  {
                    label: "Titoli a Rischio",
                    value: Math.floor(
                      Number(customer.financialData?.TITOLI_A_RISCHIO || 0)
                    ).toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                    }),
                  },
                  {
                    label: "Titoli",
                    value: Math.floor(
                      Number(customer.financialData?.TITOLI || 0)
                    ).toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                    }),
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className={`rounded-lg p-4 ${baseClasses.inputBg}`}
                  >
                    <div
                      className={`text-sm mb-1 ${baseClasses.textSecondary}`}
                    >
                      {item.label}
                    </div>
                    <div
                      className={`text-lg font-medium ${baseClasses.highlight}`}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div> */}

        {/* Address Section */}
        {customer.addresses.length > 0 && (
          <div className={`rounded-lg mb-6 border ${baseClasses.card}`}>
            <div className={`px-6 py-4 border-b ${baseClasses.border}`}>
              <h2 className={`text-lg font-medium ${baseClasses.cardHeader}`}>
                Indirizzi
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {customer.addresses.map((addr, index) => (
                  <div
                    key={index}
                    className={`rounded-lg p-4 ${baseClasses.inputBg}`}
                  >
                    <div
                      className={`text-sm font-medium mb-3 ${baseClasses.textSecondary}`}
                    >
                      Indirizzo {index + 1}
                    </div>
                    <div className="space-y-2">
                      <div className={`text-sm ${baseClasses.text}`}>
                        {addr.INDIRIZZO}
                      </div>
                      <div className={`text-sm ${baseClasses.text}`}>
                        {addr.CAP}
                      </div>
                      <div className={`text-sm ${baseClasses.text}`}>
                        {addr.CITTA}
                      </div>
                      <div className={`text-sm ${baseClasses.text}`}>
                        {addr.PROVINCIA}
                      </div>
                      {addr.TEL && (
                        <div className={`text-sm ${baseClasses.text}`}>
                          <span className={baseClasses.textSecondary}>
                            Tel:
                          </span>{" "}
                          {addr.TEL}
                        </div>
                      )}
                      {addr.EMAIL && (
                        <div className={`text-sm ${baseClasses.text}`}>
                          <span className={baseClasses.textSecondary}>
                            Email:
                          </span>{" "}
                          {addr.EMAIL}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Details Table Section */}
        <div className={`rounded-lg p-6 border ${baseClasses.card}`}>
          <h2 className={`text-2xl font-bold mb-6 ${baseClasses.cardHeader}`}>
            Dati Dettaglio
          </h2>
          <div
            className="overflow-x-auto relative table-container custom-scrollbar"
            ref={tableRef}
          >
            <table className="w-full">
              <thead className={baseClasses.inputBg}>
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${baseClasses.textSecondary} uppercase tracking-wider`}
                  >
                    Data Scadenza
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${baseClasses.textSecondary} uppercase tracking-wider`}
                  >
                    Importo
                  </th>
                  <th
                    className={`px-6 py-3 text-center text-xs font-medium ${baseClasses.textSecondary} uppercase tracking-wider`}
                  >
                    Numero Ricevuta
                  </th>
                  <th
                    className={`px-6 py-3 text-center text-xs font-medium ${baseClasses.textSecondary} uppercase tracking-wider`}
                  >
                    Tipo
                  </th>
                </tr>
              </thead>
              <tbody
                className={`${baseClasses.card} divide-y ${baseClasses.border}`}
              >
                {customer.financialDetails &&
                customer.financialDetails.length > 0 ? (
                  customer.financialDetails.map((detail, index) => (
                    <tr key={index} className={baseClasses.hover}>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${baseClasses.text}`}
                      >
                        {(() => {
                          // Funzione helper per formattare date in formato AAAAMMGG
                          const formatDateFromYYYYMMDD = (dateNumber: number | string): string => {
                            const dateStr = dateNumber.toString();
                            if (dateStr.length === 8) {
                              const year = dateStr.substring(0, 4);
                              const month = dateStr.substring(4, 6);
                              const day = dateStr.substring(6, 8);
                              return `${day}/${month}/${year}`;
                            }
                            return '';
                          };
                          
                          // Se è un numero, proviamo diverse interpretazioni
                          if (typeof detail.DATA_SCADENZA === 'number') {
                            // Prima proviamo il formato AAAAMMGG (come in Documenti.tsx)
                            if (detail.DATA_SCADENZA.toString().length === 8) {
                              const formatted = formatDateFromYYYYMMDD(detail.DATA_SCADENZA);
                              if (formatted) return formatted;
                            }
                            
                            // Formato custom YYDDD (es. 2025963 = anno 2020, giorno 259, suffisso 63)
                            if (detail.DATA_SCADENZA.toString().length === 7) {
                              const str = detail.DATA_SCADENZA.toString();
                              const yearPart = '20' + str.substring(0, 2);
                              const dayOfYear = parseInt(str.substring(2, 5));
                              const customDate = moment(yearPart + '-01-01').add(dayOfYear - 1, 'days');
                              
                              if (customDate.isValid() && customDate.year() >= 2020 && customDate.year() <= 2030) {
                                return customDate.format('DD/MM/YYYY');
                              }
                            }
                            
                            // Fallback: timestamp Unix in secondi
                            const dateFromSeconds = moment.unix(detail.DATA_SCADENZA);
                            if (dateFromSeconds.isValid() && dateFromSeconds.year() >= 1990) {
                              return dateFromSeconds.format('DD/MM/YYYY');
                            }
                            
                            // Se nessuna interpretazione funziona
                            return 'N/A';
                          }
                          
                          // Se è una stringa, proviamo a parsarla normalmente
                          if (typeof detail.DATA_SCADENZA === 'string') {
                            // Prima proviamo il formato AAAAMMGG
                            const formatted = formatDateFromYYYYMMDD(detail.DATA_SCADENZA);
                            if (formatted) return formatted;
                            
                            // Poi proviamo con moment
                            const parsed = moment(detail.DATA_SCADENZA);
                            if (parsed.isValid()) {
                              return parsed.format('DD/MM/YYYY');
                            }
                          }
                          
                          return 'N/A';
                        })()}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${baseClasses.text}`}
                      >
                        {detail.IMPORTO !== undefined
                          ? detail.IMPORTO.toLocaleString("it-IT", {
                              style: "currency",
                              currency: "EUR",
                            })
                          : "N/A"}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${baseClasses.text} text-center`}
                      >
                        {detail.NUMERO_RICEVUTA}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${baseClasses.text} text-center`}
                      >
                        {detail.TIPO}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className={`px-6 py-4 text-center text-sm ${baseClasses.textSecondary}`}
                    >
                      Nessun dato disponibile per questo cliente.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
